import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Badge,
  DropdownMenu,
  Heading,
  IconButton,
  EllipsisIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TooltipGroup,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'

import { ColumnNameForm } from './ColumnNameForm'
import { DeleteIdeaGroupAlertDialog } from './DeleteIdeaGroupAlertDialog'
import { LockedColumnMarker } from './LockedColumnMarker'

import styles from './ColumnHeader.module.css'

export const ColumnHeader = ({
  name,
  count,
  editable = true,
  locked = false,
  onCreateIdea,
  onNameChange,
  onDelete,
}: {
  name: string
  count: number
  editable?: boolean
  locked?: boolean
  onCreateIdea: () => void
  onNameChange: (name: string) => void | Promise<void>
  onDelete: () => void
}): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false)
  const canManageIdeas = useHasOrganizationAction('manageIdeas')
  const canEditHeader = editable && !locked && canManageIdeas

  const handleTriggerRename = (): void => {
    if (canEditHeader) {
      setIsEditing(true)
    }
  }

  const handleHeaderKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleTriggerRename()
    }
  }

  const handleFormSubmit = (newName: string): void => {
    setIsEditing(false)
    void onNameChange(newName)
  }

  return (
    <header className={clsx(styles.header, isEditing && styles.editing)}>
      {isEditing ? (
        <ColumnNameForm
          initialName={name}
          className={styles.form}
          onSubmit={handleFormSubmit}
          onCancel={(): void => setIsEditing(false)}
        />
      ) : (
        // Conditional button semantics and keyboard handlers are applied together
        // when the column name can be edited.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={clsx(
            styles.content,
            canEditHeader && styles.contentEditable,
          )}
          onClick={canEditHeader ? handleTriggerRename : undefined}
          onKeyDown={canEditHeader ? handleHeaderKeyDown : undefined}
          role={canEditHeader ? 'button' : undefined}
          tabIndex={canEditHeader ? 0 : undefined}
          aria-label={canEditHeader ? `Rename ${name} column` : undefined}
        >
          <Heading as="h2" size="xs" data-testid="board-column-name">
            {name}
          </Heading>
          <Badge
            variant={locked ? 'neutral' : 'light'}
            className={styles.badge}
          >
            {count}
          </Badge>
        </div>
      )}

      <div className={styles.actions}>
        <TooltipGroup>
          {!locked && canManageIdeas && (
            <IconButton
              label="Add new idea"
              variant="tertiary"
              onClick={onCreateIdea}
              tooltip="Add new idea"
            >
              <PlusIcon />
            </IconButton>
          )}
          {locked && <LockedColumnMarker />}

          {editable && canManageIdeas && (
            <DropdownMenu
              modal={false}
              trigger={
                <IconButton label="More" variant="tertiary" tooltip="More">
                  <EllipsisIcon />
                </IconButton>
              }
            >
              {!locked && (
                <>
                  <DropdownMenu.Item onSelect={handleTriggerRename}>
                    <PencilIcon />
                    Rename
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />
                </>
              )}
              <DeleteIdeaGroupAlertDialog
                name={name}
                ideasCount={count}
                onDelete={onDelete}
              >
                <DropdownMenu.Item
                  variant="critical"
                  // Needed for item to be able to open dialog
                  onSelect={(e): void => e.preventDefault()}
                  description='Delete group and move ideas to "Unassigned"'
                >
                  <TrashIcon />
                  Delete
                </DropdownMenu.Item>
              </DeleteIdeaGroupAlertDialog>
            </DropdownMenu>
          )}
        </TooltipGroup>
      </div>
    </header>
  )
}
